import type { ConnectionType } from '../types/connection.types';

/**
 * Connector categories that gate access to catalog operations and experiment
 * creation. These lists are the single source of truth for "what counts" in
 * each category — used by:
 *   - `RequireChannelConnection` (catalog-operations route gate; shows the
 *     connect-channel placeholder when no INPUT_SOURCE is connected)
 *   - `useRequiredExperimentConnectors` (button-level experiment eligibility;
 *     requires at least one Input source AND at least one of Ads OR Analytics)
 *
 * Note: `useStartExperimentCreation` maintains its own `GOOGLE_CATALOG_PRIORITY`
 * list to keep GMC-first experiment source selection isolated from catalog-ops
 * gating (META_COMMERCE is valid for catalog-ops but not for Google Ads flows).
 *
 * `as const` so TypeScript narrows membership checks and downstream callers
 * can index without widening.
 */

/** Catalog data sources. Any one connected → catalog operations enabled.
 *  Priority order (GMC first) matches `resolveCatalogSource` in onboarding. */
export const INPUT_SOURCES: readonly ConnectionType[] = [
  'GOOGLE_MERCHANT',
  'META_COMMERCE',
  'SHOPIFY',
  'MANUAL_UPLOAD',
] as const;

/** Ad-platform sources. Required for experiment creation (not catalog ops). */
export const ADS_SOURCES: readonly ConnectionType[] = ['GOOGLE_ADS'] as const;

/** Analytics sources. Required for experiment creation (not catalog ops). */
export const ANALYTICS_SOURCES: readonly ConnectionType[] = [
  'GA4_ANALYTICS',
  'GA4_REPORTING',
] as const;

/** Catalog sources that support a scoped ("import a limited catalog") import —
 *  i.e. the backend accepts a `productIds` subset on `POST /products/sync` for
 *  these connectors. GMC shipped it first; META_COMMERCE reached parity on the
 *  meta-readiness work. Any other source keeps the full-import-only overview. */
export const SCOPED_IMPORT_SOURCES: readonly ConnectionType[] = [
  'GOOGLE_MERCHANT',
  'META_COMMERCE',
] as const;

/** Whether a (possibly-undefined) connector code supports scoped import. Written
 *  as a type guard so the three scoped-import call sites can pass the resolved
 *  `ConnectionType | undefined` and still hand a concrete code to the mutation. */
export function isScopedImportSource(code: ConnectionType | undefined): code is ConnectionType {
  return code != null && SCOPED_IMPORT_SOURCES.includes(code);
}
